/**
 * The login strips (spec 10.1, CONTEXT.md "Login strip"): the printable slip each student is
 * handed once, carrying their username and PIN.
 *
 * PINs are hashed in the database, so this screen is the only moment they exist in plain text
 * — it renders what the create or reset response just returned, passed through router
 * navigation state. Nothing is stored and nothing is refetched, and the teacher's way back is
 * "PIN zurücksetzen".
 *
 * The state is read once into component state and then *erased from the history entry*, which
 * is not a detail: React Router keeps navigation state in `history.state.usr`, and the browser
 * restores that on a reload and on session restore. Without the erase, a reload would put four
 * plain-text PINs per child back on the screen, and they would sit in the browser's session
 * history on a shared staffroom laptop — the opposite of what spec 10.1 asks for.
 */

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { APP_URL } from "../shared/app-url.js";
import { printPage } from "../shared/platform.js";
import { buttonClasses, Panel, TButton } from "./ui.js";

export interface StripData {
  classNumber: number;
  classLabel: string;
  students: { username: string; pin: string }[];
}

/** Build spec 5: 2 x 5 per A4 page. */
const STRIPS_PER_PAGE = 10;

function isStripData(value: unknown): value is StripData {
  if (typeof value !== "object" || value === null) return false;
  const data = value as Partial<StripData>;
  return (
    typeof data.classNumber === "number" &&
    typeof data.classLabel === "string" &&
    Array.isArray(data.students)
  );
}

export function StripsScreen() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Captured before the effect below erases it, so this render keeps the PINs in memory while
  // the history entry loses them.
  const [data] = useState<StripData | null>(() =>
    isStripData(location.state) ? location.state : null,
  );

  useEffect(() => {
    if (location.state) {
      void navigate(location.pathname, { replace: true, state: null });
    }
    // Once, on mount: re-running on every navigate would fight the replace it just did.
  }, []);

  if (!data) {
    return (
      <Panel title="Anmeldestreifen">
        <div className="flex flex-col items-start gap-3 px-4 py-6">
          <p className="m-0 text-[15px] leading-relaxed text-[var(--color-ink)]">
            Die PINs sind nur einmal sichtbar, direkt nachdem du Schüler angelegt oder einen PIN
            zurückgesetzt hast. Diese Seite lässt sich nicht neu laden.
          </p>
          <p className="m-0 text-[15px] text-[var(--color-muted)]">
            Brauchst du den Streifen nochmal, setze den PIN in der Klasse zurück.
          </p>
          <Link
            to={"/lehrer/klassen/" + id}
            className={buttonClasses("primary", "mt-1 inline-block no-underline")}
          >
            Zurück zur Klasse
          </Link>
        </div>
      </Panel>
    );
  }

  const pages: StripData["students"][] = [];
  for (let i = 0; i < data.students.length; i += STRIPS_PER_PAGE) {
    pages.push(data.students.slice(i, i + STRIPS_PER_PAGE));
  }

  return (
    <>
      <div className="no-print mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <nav className="mb-1 text-[14px]">
            <Link
              to={"/lehrer/klassen/" + id}
              className="text-[var(--color-accent)] no-underline hover:underline"
            >
              Klasse {data.classLabel}
            </Link>
            <span className="text-[var(--color-muted)]"> / Anmeldestreifen</span>
          </nav>
          <h1 className="m-0 text-[26px] font-bold">{data.students.length} Streifen</h1>
        </div>
        <TButton variant="primary" onClick={() => printPage()}>
          Drucken
        </TButton>
      </div>

      <p className="no-print mb-5 max-w-2xl text-[15px] leading-relaxed text-[var(--color-muted)]">
        Drucke die Streifen jetzt aus und gib jedem Kind seinen. Die PINs sind verschlüsselt
        gespeichert und hier zum letzten Mal zu sehen — sobald du die Seite verlässt, sind sie weg.
      </p>

      <div className="print-sheet">
        {pages.map((page, pageIndex) => (
          <div
            key={pageIndex}
            className="print-page mb-6 grid grid-cols-2 gap-3 last:mb-0"
            aria-label={"Seite " + (pageIndex + 1)}
          >
            {page.map((student) => (
              <Strip
                key={student.username}
                username={student.username}
                pin={student.pin}
                classNumber={data.classNumber}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

function Strip({
  username,
  pin,
  classNumber,
}: {
  username: string;
  pin: string;
  classNumber: number;
}) {
  return (
    <div className="strip rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] p-3.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[13px] font-black">Scrolivo</span>
        <span className="text-[11px] text-[var(--color-muted)]">{APP_URL}</span>
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] text-[var(--color-muted)]">Klasse</div>
          <div className="t-num text-[20px] font-bold">{classNumber}</div>
        </div>
        <div className="flex-1">
          <div className="text-[11px] text-[var(--color-muted)]">Benutzername</div>
          <div className="text-[15px] font-bold break-all">{username}</div>
        </div>
        <div>
          <div className="text-[11px] text-[var(--color-muted)]">PIN</div>
          <div className="t-num text-[22px] font-black tracking-[0.15em]">{pin}</div>
        </div>
      </div>
      <p className="mt-2 mb-0 text-[11px] leading-snug text-[var(--color-muted)]">
        1. Klassennummer {classNumber} eintippen · 2. auf deinen Namen tippen · 3. PIN eintippen
      </p>
    </div>
  );
}
